/**
 * WholetailFormulaCard — sits at the top of the Wholetail tab and
 * anchors the operator on the one canonical Wholetail MAO number
 * (from `/api/v3/properties/:id/metrics`, the golden metrics endpoint).
 *
 * The rest of the Wholetail tab does deep BP-010 analysis; THIS card
 * exists to answer "what's the number I can safely offer the seller?"
 * in one glance, with the plain-english breakdown of the formula.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Info, DollarSign, TrendingUp, ArrowRight, HelpCircle } from "lucide-react";

const fmt = (n) => (n == null ? "—" : `$${Math.round(Number(n)).toLocaleString()}`);
const pct = (n) => `${(n * 100).toFixed(0)}%`;

// Same constants as `services/deal_metrics.py`; kept in sync at review-time.
const CONST = {
  closing_pct:      0.07,
  holding_pct:      0.05,
  buyer_profit_pct: 0.10,
};

export default function WholetailFormulaCard({ propertyId }) {
  const [metrics, setMetrics] = useState(null);
  const [showMath, setShowMath] = useState(false);

  useEffect(() => {
    if (!propertyId) return;
    let alive = true;
    api.get(`/v3/properties/${propertyId}/metrics`)
      .then((r) => { if (alive) setMetrics(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [propertyId]);

  const arv = metrics?.inputs?.arv;
  const fee = metrics?.inputs?.assignment_fee;
  const minor = metrics?.inputs?.repairs ? Math.round(metrics.inputs.repairs * 0.4) : null;
  const wholetail = metrics?.wholetail;
  const wholesale = metrics?.wholesale;

  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-indigo-50 to-white border border-indigo-100 p-5 mb-4"
      data-testid="wholetail-formula-card"
    >
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex-1 min-w-[280px]">
          <div className="text-[11px] font-black uppercase tracking-[0.16em] text-indigo-700 mb-1">
            Canonical Wholetail Offer
          </div>
          <h3 className="text-lg font-black text-slate-900 leading-tight mb-1">
            Wholetail = acquire cheap, cosmetic-only, retail resale
          </h3>
          <p className="text-[13px] text-slate-600 leading-relaxed">
            When the deal doesn't justify a full flip rehab but there's enough ARV
            headroom to list retail. We build in <b>7 % closing</b>, <b>5 % holding</b>,
            and a <b>10 % retail-buyer profit slice</b> on top of your assignment fee,
            then deduct <b>40 %</b> of the full-rehab estimate as the cosmetic-only spend.
          </p>
        </div>

        {/* MAO tile */}
        <div className="rounded-xl bg-indigo-600 text-white p-4 min-w-[220px] shadow-md shadow-indigo-900/25">
          <div className="text-[10px] font-black uppercase tracking-widest opacity-80">
            Wholetail MAO
          </div>
          <div className="text-3xl font-black tabular-nums flex items-center gap-1.5" data-testid="wholetail-mao-value">
            <DollarSign className="w-6 h-6 opacity-80" />
            {fmt(wholetail?.mao).replace("$", "")}
          </div>
          <div className="text-[11px] opacity-85 mt-1">
            {wholetail?.feasible === false ? "Deal doesn't pencil for wholetail" : "Max you can safely offer the seller"}
          </div>
        </div>
      </div>

      {/* Comparison + math toggle */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
        <MiniCompare label="Flip 70 MAO"   value={fmt(metrics?.flip_70?.mao)} />
        <MiniCompare label="Wholesale MAO" value={fmt(wholesale?.mao)} highlight />
        <MiniCompare label="Wholetail MAO" value={fmt(wholetail?.mao)} />
      </div>

      <button
        type="button"
        onClick={() => setShowMath((v) => !v)}
        className="mt-3 inline-flex items-center gap-1 text-[12px] font-bold text-indigo-700 hover:text-indigo-900"
        data-testid="wholetail-formula-toggle"
      >
        <HelpCircle className="w-3.5 h-3.5" />
        {showMath ? "Hide the math" : "Show the math"}
      </button>

      {showMath && (
        <div className="mt-3 rounded-lg bg-white border border-slate-200 p-3 text-[12.5px] font-mono text-slate-700 leading-relaxed">
          <Row label="ARV"                          val={fmt(arv)} sign="" />
          <Row label={`Closing costs (${pct(CONST.closing_pct)} × ARV)`} val={arv ? fmt(-arv * CONST.closing_pct) : "—"} sign="−" />
          <Row label="Minor repairs (~40 % of full rehab)" val={fmt(minor ? -minor : null)} sign="−" />
          <Row label={`Holding costs (${pct(CONST.holding_pct)} × ARV)`} val={arv ? fmt(-arv * CONST.holding_pct) : "—"} sign="−" />
          <Row label={`Retail-buyer profit (${pct(CONST.buyer_profit_pct)} × ARV)`} val={arv ? fmt(-arv * CONST.buyer_profit_pct) : "—"} sign="−" />
          <Row label="Assignment fee"               val={fmt(fee ? -fee : null)} sign="−" />
          <div className="border-t border-slate-200 mt-1.5 pt-1.5 flex items-center justify-between font-bold text-indigo-800">
            <span>Wholetail MAO</span><span className="tabular-nums">{fmt(wholetail?.mao)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function MiniCompare({ label, value, highlight }) {
  return (
    <div className={`rounded-lg px-3 py-2 border ${highlight ? "border-indigo-300 bg-indigo-50" : "border-slate-200 bg-white"}`}>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-[14px] font-black tabular-nums ${highlight ? "text-indigo-800" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function Row({ label, val, sign }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-600">{sign} {label}</span>
      <span className="tabular-nums text-slate-900">{val}</span>
    </div>
  );
}
